import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isPinSession, getPinSessionData } from '@/lib/pin-session-utils';
import { invokeLimitedFunction } from '@/lib/limited-api';
import { createSingleFlight } from '@/lib/single-flight';
import {
  FEATURE_CACHE_TTL_MS,
  FEATURE_FOCUS_STALE_MS,
  FEATURE_STALE_MS,
} from '@/lib/feature-access-cache';

interface UseFeatureAccessReturn {
  hasAccess: boolean;
  loading: boolean;
}

const featureAccessCache = new Map<string, boolean>();

// In-flight limited-feature-access calls, keyed by company. THIS is the fix for
// the call volume on a kiosk login. Both caches above and below are written
// only when a call RETURNS, so the SIX useFeatureAccessForPIN instances
// EmployeeLimitedView mounts in one tick all missed and each fired its own
// request -- and every one of them asks for the WHOLE feature list, which
// limited-feature-access resolves with one has_feature_access RPC per feature.
// That is 6 edge invocations and ~84 RPCs per login, against the same
// 60-connection instance that has_feature_access already had to be rescued
// from once (see src/lib/feature-access-cache.ts). It used to fire -- and
// BLOCK the kiosk -- on every login, because the `pin_session_updated` handler
// wiped the caches first; see onPinSessionChanged below for what replaced that.
//
// The JWT hook (use-feature-access.ts) has had this dedup since that incident;
// the PIN path is the half that never got it.
const featureFlight = createSingleFlight<Record<string, boolean>>();

// Persistent localStorage cache for feature access (survives page reloads).
// Keys are scoped by company_id so a stale entry from one customer can't
// leak into another (which was the bug the old universal-optimistic-grant
// block was patching around). Companies are isolated; switching kiosks
// between accounts now just gets a fresh fetch instead of poisoned data.
const CACHE_KEY_PREFIX = 'feature_access_cache_v2_';
const CACHE_EXPIRY_PREFIX = 'feature_access_cache_expiry_v2_';
// When the whole company map was last confirmed by a successful
// limited-feature-access response. Missing on caches written before
// background re-checks existed, and those are treated as stale so a reload
// asks again instead of trusting the six-hour expiry.
export const PIN_FEATURE_CHECKED_PREFIX = 'feature_access_cache_checked_v2_';
const LEGACY_CACHE_KEY = 'feature_access_cache';
const LEGACY_EXPIRY_KEY = 'feature_access_cache_expiry';

function cacheKeyFor(companyId: string) {
  return `${CACHE_KEY_PREFIX}${companyId}`;
}
function expiryKeyFor(companyId: string) {
  return `${CACHE_EXPIRY_PREFIX}${companyId}`;
}
function checkedKeyFor(companyId: string) {
  return `${PIN_FEATURE_CHECKED_PREFIX}${companyId}`;
}

// In-memory twin of the checked key, so a private-mode kiosk (no localStorage)
// still knows when the answer it is showing was confirmed.
const pinConfirmedAt = new Map<string, number>();

function getLocalStorageCache(companyId: string | null): Record<string, boolean> | null {
  if (!companyId) return null;
  try {
    const cached = localStorage.getItem(cacheKeyFor(companyId));
    if (!cached) return null;
    const parsed = JSON.parse(cached);
    // Keep the last saved map even past the old six-hour expiry. Deleting it
    // on read was how a failed re-check turned into "feature off": the answer
    // was gone, then the cold fetch failed closed. Expiry is no longer a miss.
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Record<string, boolean>;
  } catch {
    return null;
  }
}

function setLocalStorageCache(
  companyId: string | null,
  features: Record<string, boolean>,
  companyKey: string,
  confirm: boolean,
) {
  const now = Date.now();
  // `confirm` is false when this response resolved nothing. Keep the previous
  // confirmation clock so the next focus or interval tries again, instead of
  // treating a total miss as a fresh answer.
  if (confirm) pinConfirmedAt.set(companyKey, now);
  if (!companyId) return;
  try {
    localStorage.setItem(cacheKeyFor(companyId), JSON.stringify(features));
    // Persistence horizon only. Freshness is the checked key below, and it
    // moves for the whole map because this call is one company-wide answer.
    localStorage.setItem(expiryKeyFor(companyId), (now + FEATURE_CACHE_TTL_MS).toString());
    if (confirm) localStorage.setItem(checkedKeyFor(companyId), String(now));
  } catch (error) {
    console.warn('[FEATURE_CACHE] Failed to save cache:', error);
  }
}

// Map feature names to plan feature keys. This map is ALSO the fetch list:
// see planKeyRequestNames() below.
const FEATURE_MAPPING: Record<string, string> = {
  'communications': 'team_communications',
  'tasks': 'task_management',
  'training': 'training_library_sops',
  'maintenance': 'maintenance_tracking',
  'integrations_access': 'pos_scheduling_integrations',
  'daily_checklists': 'daily_checklists',
  'checklists': 'daily_checklists',
  'gamification': 'gamification',
  'kiosk': 'kiosk_login',
  'mobile': 'mobile_app_access',
  'analytics': 'analytics',
  'schedules': 'schedules',
  'temperature_logs': 'temperature_logs',
  // Identity mapping — has_feature_access has no alias for `surveys`,
  // so plan key == feature name. Listed here (rather than falling
  // through) because this map is also the fetch list: a key that isn't
  // in it is never requested from limited-feature-access, so the kiosk
  // Surveys tile would resolve false for everyone.
  'surveys': 'surveys',
  // Identity mapping, same reasoning as `surveys` above: unaliased in
  // has_feature_access, and listed here because this map is the fetch
  // list. Cash tips are declared at CLOCK-OUT, which is a kiosk moment,
  // so the key a kiosk surface will ask for has to be requested here or
  // it would resolve false for the JIU stores that do have it on.
  'tip_pooling': 'tip_pooling',
  // Identity mappings, same reason as `surveys`: unaliased in
  // has_feature_access, and this map is the fetch list. A key missing here
  // is never requested, so the kiosk gate would resolve false for everyone
  // even after the plan backfill turns it on.
  'daily_logs': 'daily_logs',
  'par_sheets': 'par_sheets',
  'my_agreements': 'my_agreements',
  'employee_files': 'employee_files',
  'brand_standards': 'brand_standards',
  // Identity mapping, same reason as the group above. Required Team Comms
  // reading opens on a kiosk, so the key has to be in this fetch list or
  // every tenant would resolve false. See src/lib/company-features.ts.
  'required_team_log_reading': 'required_team_log_reading',
};

// Plan key -> the one feature NAME we ask limited-feature-access for it.
//
// FEATURE_MAPPING is deliberately MANY-to-one: `checklists` and
// `daily_checklists` are two names for the single plan key `daily_checklists`,
// and has_feature_access resolves both through the same branch. Ask once per
// PLAN KEY rather than once per name. The duplicate was not just a wasted
// round-trip: limited-feature-access resolves each requested name with its own
// has_feature_access call and substitutes `false` for any one that errors, so
// two names for the same key can come back disagreeing.
//
// Hoisted to module scope (it used to be rebuilt inside every checkAccess) so
// the background revalidation below, which runs outside any hook instance,
// asks for exactly the same set the gates do.
function planKeyRequestNames(): Map<string, string> {
  const out = new Map<string, string>();
  for (const [name, planKey] of Object.entries(FEATURE_MAPPING)) {
    if (!out.has(planKey)) out.set(planKey, name);
  }
  return out;
}

/**
 * Which company the PIN session on this device belongs to. The profile's
 * company_id is the fallback for a JWT-authenticated caller.
 */
function resolveCompany(profileCompanyId?: string | null): { companyId: string | null; companyCode: string | null } {
  let companyId: string | null = null;
  let companyCode: string | null = null;
  if (isPinSession()) {
    const pinData = getPinSessionData();
    companyId = pinData?.profile?.company_id || pinData?.company?.id || null;
    companyCode = pinData?.company_code || null;
  }
  if (!companyId && profileCompanyId) companyId = profileCompanyId;
  return { companyId, companyCode };
}

/** The persisted answer for one gate, or undefined when we have none. */
function readCachedAccess(companyId: string | null, featureName: string): boolean | undefined {
  const localCache = getLocalStorageCache(companyId);
  if (!localCache) return undefined;
  const mappedFeature = FEATURE_MAPPING[featureName] || featureName;
  return mappedFeature in localCache ? localCache[mappedFeature] : undefined;
}

// Fired (same tab) after a limited-feature-access answer lands in the cache,
// so every mounted gate for that company can pick it up. This is how a
// BACKGROUND revalidation reaches gates that already painted from the cache:
// nobody is awaiting that request, so without a broadcast a revoked feature
// would stay visible until the next mount.
const CACHE_UPDATED_EVENT = 'feature_access_pin_cache_updated';

// When each company's last limited-feature-access call STARTED. Used only to
// throttle the login-time revalidation: `pin_session_updated` is also
// dispatched by things that are not logins (MainLayout, PaginatedTasksList),
// and a burst of those must not turn into a burst of ~14-RPC calls.
const lastFetchStartedAt = new Map<string, number>();
// Same floor as a reload / refocus re-check. A burst of pin_session_updated
// (MainLayout, PaginatedTasksList) must not each become a ~20-RPC call.
const REVALIDATE_MIN_INTERVAL_MS = FEATURE_FOCUS_STALE_MS;

/**
 * Ask limited-feature-access for the whole feature list for a company, and
 * write the answer everywhere it is read from: the localStorage tier, the
 * in-memory Map, and (via CACHE_UPDATED_EVENT) any gate already on screen.
 *
 * Runs inside the single-flight, so however many gates -- plus the login
 * revalidation -- ask at once, it is one request and one cache write.
 * Resolves to the FRESH plan-key map (only what this response resolved), so a
 * caller can tell "denied" apart from "not answered this time". Rejects on a
 * transport or function error.
 */
function fetchPlanFeatures(companyId: string | null, companyCode: string | null): Promise<Record<string, boolean>> {
  const flightKey = companyId || companyCode || 'unknown';
  return featureFlight.run(flightKey, async () => {
    lastFetchStartedAt.set(flightKey, Date.now());
    const requestNames = planKeyRequestNames();
    const { data, error } = await invokeLimitedFunction('limited-feature-access', {
      company_id: companyId,
      company_code: companyCode,
      features: Array.from(requestNames.values()),
    });

    console.log(`[FEATURE_ACCESS_PIN] Response for all features:`, { data, error });

    if (error) {
      console.error(`[FEATURE_ACCESS_PIN] Error fetching features:`, error);
      throw new Error(`Feature access check failed: ${error}`);
    }

    const featuresData = data?.features || {};
    // limited-feature-access reports a feature it could NOT resolve in
    // `unresolved` rather than folding it into `features` as `false`.
    // An older deployment of that function omits the field — it also
    // omits nothing from `features`, so the `in` check below is what
    // actually covers both contracts.
    const unresolved: string[] = Array.isArray(data?.unresolved) ? data.unresolved : [];

    // Convert to plan feature format. Keyed off the request map, so each plan
    // key takes its value from the one name we actually asked for. Iterating
    // FEATURE_MAPPING here instead let a second alias overwrite the first
    // purely by key order — `checklists` always landed after
    // `daily_checklists`, so a single errored RPC (or a response omitting that
    // name) turned the shared gate false and parked it in the 6-hour
    // localStorage cache.
    //
    // A name we got no answer for is LEFT OUT rather than recorded as false.
    // This cache is shared by every gate for the company and lives 6 hours, so
    // writing a guess here is exactly how a transient RPC failure used to cost
    // a store its checklists for a whole shift.
    const planFeatures: Record<string, boolean> = {};
    for (const [planKey, requestName] of requestNames) {
      if (unresolved.includes(requestName)) continue;
      if (!(requestName in featuresData)) continue;
      planFeatures[planKey] = featuresData[requestName] === true;
    }

    // MERGE over what was already cached rather than replacing it. On a cold
    // cache this is identical to a plain write. On a revalidation it means a
    // key this response could not resolve keeps its last KNOWN answer instead
    // of vanishing -- vanishing would send that gate back to a cold,
    // page-blocking fetch on its next mount, i.e. undo the whole point of
    // keeping the cache across logins.
    const previous = getLocalStorageCache(companyId) || {};
    // A response that resolved nothing is not a confirmation. One that
    // resolved some keys is: unresolved names were left out of planFeatures
    // and keep whatever `previous` had, and the next interval re-checks the
    // company again once this confirmation is a few minutes old.
    setLocalStorageCache(
      companyId,
      { ...previous, ...planFeatures },
      flightKey,
      Object.keys(planFeatures).length > 0,
    );

    // Keep the in-memory tier in step. It is only consulted when localStorage
    // is unavailable (private mode) or missing a key, but when it IS consulted
    // it must not contradict what was just learned.
    for (const [name, planKey] of Object.entries(FEATURE_MAPPING)) {
      if (planKey in planFeatures) featureAccessCache.set(`${flightKey}-${name}`, planFeatures[planKey]);
    }

    try {
      window.dispatchEvent(new CustomEvent(CACHE_UPDATED_EVENT, { detail: { companyKey: flightKey, planFeatures } }));
    } catch {
      /* no window (SSR) — nothing mounted to notify */
    }

    return planFeatures;
  });
}

/**
 * Drop cached answers for every company EXCEPT `keepCompanyKey`.
 *
 * This used to be a wipe of EVERYTHING, run on every `pin_session_updated` —
 * i.e. on every single PIN login. So even the same employee at the same store
 * signing back in two minutes later found an empty cache, and the kiosk sat on
 * its full-screen spinner behind six gates until limited-feature-access
 * (~14 has_feature_access RPCs, 5-second failsafe) came back. The per-company
 * keys already make cross-tenant leakage impossible, so the wipe bought
 * nothing on a same-company login; it is now scoped to a company CHANGE.
 *
 * Deliberately does NOT clear featureFlight any more. Flights are keyed per
 * company, so a new company can never join an old one's request.
 */
function clearOtherCompaniesCache(keepCompanyKey: string) {
  for (const key of Array.from(featureAccessCache.keys())) {
    if (!key.startsWith(`${keepCompanyKey}-`)) featureAccessCache.delete(key);
  }
  for (const key of Array.from(pinConfirmedAt.keys())) {
    if (key !== keepCompanyKey) pinConfirmedAt.delete(key);
  }
  try {
    localStorage.removeItem(LEGACY_CACHE_KEY);
    localStorage.removeItem(LEGACY_EXPIRY_KEY);
    const keep = new Set([
      cacheKeyFor(keepCompanyKey),
      expiryKeyFor(keepCompanyKey),
      checkedKeyFor(keepCompanyKey),
    ]);
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key || keep.has(key)) continue;
      if (
        key.startsWith(CACHE_KEY_PREFIX) ||
        key.startsWith(CACHE_EXPIRY_PREFIX) ||
        key.startsWith(PIN_FEATURE_CHECKED_PREFIX)
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable (private mode, etc.) — in-memory clear above still ran
  }
}

/**
 * The PIN session changed (a login, a logout, or another tab doing either).
 *
 *   - Logged OUT (no company): keep everything. Entries are keyed by company,
 *     so the next user can only ever read their own company's answers, and a
 *     same-store login right after -- the normal kiosk rhythm -- paints from
 *     them instantly.
 *   - Logged in to company X: drop other companies' entries, then revalidate
 *     X in the BACKGROUND. Gates render from the cache straight away
 *     (stale-while-revalidate) and pick up any change via CACHE_UPDATED_EVENT.
 *     A cold cache is unaffected: those gates miss, stay fail-closed on the
 *     spinner, and simply join this same in-flight request.
 *
 * Registered ONCE at module scope rather than per hook instance: six mounted
 * gates used to mean six identical handlers per event, and six same-tick
 * revalidation attempts.
 */
function onPinSessionChanged(event: Event) {
  // Every localStorage write in ANOTHER tab raises `storage` -- including this
  // hook's own cache writes there. Reacting to those would ping-pong
  // revalidations between tabs, so only a change to the session itself counts
  // (key === null is a localStorage.clear()).
  if (event.type === 'storage') {
    const key = (event as StorageEvent).key;
    if (key !== null && key !== 'pin_session') return;
  }
  if (!isPinSession()) return;
  const { companyId, companyCode } = resolveCompany(null);
  const companyKey = companyId || companyCode;
  if (!companyKey) return;

  clearOtherCompaniesCache(companyKey);

  const last = lastFetchStartedAt.get(companyKey);
  if (last !== undefined && Date.now() - last < REVALIDATE_MIN_INTERVAL_MS) return;
  fetchPlanFeatures(companyId, companyCode).catch((error) => {
    // Background only: gates keep the answer they have. A cold gate that
    // joined this request handles the failure itself (retry + failsafe).
    console.warn('[FEATURE_ACCESS_PIN] Background revalidation failed:', error);
  });
}

function readPinConfirmedAt(companyId: string | null, companyKey: string): number | null {
  const mem = pinConfirmedAt.get(companyKey);
  if (mem !== undefined) return mem;
  if (!companyId) return null;
  try {
    const raw = localStorage.getItem(checkedKeyFor(companyId));
    const n = raw ? Number.parseInt(raw, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function hasPinCachedAnswer(companyId: string | null, companyKey: string): boolean {
  if (companyId && getLocalStorageCache(companyId)) return true;
  for (const key of featureAccessCache.keys()) {
    if (key.startsWith(`${companyKey}-`)) return true;
  }
  return false;
}

/**
 * Re-check a company we are already showing, at most once a minute, and only
 * when the saved answer is old enough. A throw inside fetchPlanFeatures leaves
 * the cache as it was: the last answer stays on screen.
 * No saved answer: return without fetching. The cold path in the hook owns
 * that request, and a second one here would double it on a signed-out pad.
 */
function schedulePinRevalidate(
  companyId: string | null,
  companyCode: string | null,
  minAgeMs: number,
) {
  const companyKey = companyId || companyCode;
  if (!companyKey) return;
  if (!hasPinCachedAnswer(companyId, companyKey)) return;
  const confirmed = readPinConfirmedAt(companyId, companyKey);
  if (confirmed !== null && Date.now() - confirmed < minAgeMs) return;
  const last = lastFetchStartedAt.get(companyKey);
  if (last !== undefined && Date.now() - last < REVALIDATE_MIN_INTERVAL_MS) return;
  fetchPlanFeatures(companyId, companyCode).catch((error) => {
    console.warn('[FEATURE_ACCESS_PIN] Background revalidation failed:', error);
  });
}

function revalidateOpenPinSession(minAgeMs: number) {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (!isPinSession()) return;
  const { companyId, companyCode } = resolveCompany(null);
  schedulePinRevalidate(companyId, companyCode, minAgeMs);
}

// One timer for every mounted gate. Cleared when the last gate unmounts so
// a test (and a signed-out screen) does not keep polling. Jitter keeps a
// fleet of kiosks from asking on the same tick.
let pinMounts = 0;
let pinTimer: number | undefined;
let pinForegroundBound = false;
const PIN_INTERVAL_JITTER_MS = Math.floor(Math.random() * FEATURE_FOCUS_STALE_MS);

function syncPinTimer() {
  if (typeof window === 'undefined') return;
  if (pinMounts <= 0) {
    if (pinTimer !== undefined) {
      window.clearTimeout(pinTimer);
      pinTimer = undefined;
    }
    return;
  }
  if (pinTimer !== undefined) return;
  pinTimer = window.setTimeout(() => {
    pinTimer = undefined;
    revalidateOpenPinSession(FEATURE_STALE_MS);
    syncPinTimer();
  }, FEATURE_STALE_MS + PIN_INTERVAL_JITTER_MS);
}

function ensurePinForegroundListener() {
  if (pinForegroundBound || typeof window === 'undefined') return;
  pinForegroundBound = true;
  window.addEventListener('focus', () => revalidateOpenPinSession(FEATURE_FOCUS_STALE_MS));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') revalidateOpenPinSession(FEATURE_FOCUS_STALE_MS);
  });
}

function retainPinWatch() {
  pinMounts += 1;
  ensurePinForegroundListener();
  syncPinTimer();
  return () => {
    pinMounts -= 1;
    syncPinTimer();
  };
}

try {
  if (typeof window !== 'undefined') {
    window.addEventListener('pin_session_updated', onPinSessionChanged);
    window.addEventListener('storage', onPinSessionChanged);
  }
} catch {
  /* SSR or unavailable */
}

// One-time legacy migration: blow away the unscoped pre-v2 entries that
// could carry data from any company. This runs on module load, not per
// hook, so it's effectively a no-op once cleared.
try {
  if (typeof localStorage !== 'undefined' && localStorage.getItem(LEGACY_CACHE_KEY)) {
    localStorage.removeItem(LEGACY_CACHE_KEY);
    localStorage.removeItem(LEGACY_EXPIRY_KEY);
    console.log('[FEATURE_ACCESS_PIN] Cleared legacy unscoped localStorage cache');
  }
} catch {
  /* SSR or unavailable */
}

export function useFeatureAccessForPIN(featureName: string): UseFeatureAccessReturn {
  const { profile } = useAuth();
  // Seeded from the persisted cache when it already holds this gate, so a warm
  // kiosk paints its tiles on the FIRST render instead of showing the
  // full-screen spinner for a frame (or, before the cache survived logins, for
  // the whole limited-feature-access round trip). A cold cache starts exactly
  // as before: loading=true, hasAccess=false -- we never show a feature before
  // we know the store has it.
  const [initialCached] = useState<boolean | undefined>(() =>
    readCachedAccess(resolveCompany(profile?.company_id).companyId, featureName)
  );
  const [hasAccess, setHasAccess] = useState(initialCached === true);
  const [loading, setLoadingState] = useState(initialCached === undefined);
  // Mirrors `loading` for the failsafe timer, which is armed inside an effect
  // and would otherwise read the value from the render that armed it.
  const loadingRef = useRef(initialCached === undefined);
  const setLoading = (value: boolean) => {
    loadingRef.current = value;
    setLoadingState(value);
  };
  const retryCount = useRef(0);
  const maxRetries = 3;
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [pinSessionCompanyId, setPinSessionCompanyId] = useState<string | null>(() => {
    if (isPinSession()) {
      const pinData = getPinSessionData();
      return pinData?.profile?.company_id || pinData?.company?.id || null;
    }
    return null;
  });

  useEffect(() => retainPinWatch(), []);

  useEffect(() => {
    // Cache invalidation is NOT done here any more -- it lives in the single
    // module-level onPinSessionChanged listener above, which only drops OTHER
    // companies' entries. This per-instance handler just re-points the gate
    // at whichever company the session now belongs to.
    const handlePinSessionUpdate = (event: Event) => {
      if (event.type === 'storage') {
        const key = (event as StorageEvent).key;
        if (key !== null && key !== 'pin_session') return;
      }
      retryCount.current = 0;
      if (isPinSession()) {
        const pinData = getPinSessionData();
        const newCompanyId = pinData?.profile?.company_id || pinData?.company?.id || null;
        setPinSessionCompanyId(newCompanyId);
      } else {
        setPinSessionCompanyId(null);
      }
    };

    window.addEventListener('pin_session_updated', handlePinSessionUpdate);
    window.addEventListener('storage', handlePinSessionUpdate);

    return () => {
      window.removeEventListener('pin_session_updated', handlePinSessionUpdate);
      window.removeEventListener('storage', handlePinSessionUpdate);
    };
  }, []);

  // A limited-feature-access answer landed -- possibly a background
  // revalidation this gate never asked for. Adopt it if it is for OUR company
  // and actually resolved our key; an unresolved key leaves the current
  // answer alone rather than flipping it to a guess.
  useEffect(() => {
    const handleCacheUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ companyKey: string; planFeatures: Record<string, boolean> }>).detail;
      if (!detail) return;
      const { companyId, companyCode } = resolveCompany(profile?.company_id);
      if (detail.companyKey !== (companyId || companyCode || 'unknown')) return;
      const mappedFeature = FEATURE_MAPPING[featureName] || featureName;
      if (!(mappedFeature in detail.planFeatures)) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      setHasAccess(detail.planFeatures[mappedFeature] === true);
      setLoading(false);
    };
    window.addEventListener(CACHE_UPDATED_EVENT, handleCacheUpdated);
    return () => window.removeEventListener(CACHE_UPDATED_EVENT, handleCacheUpdated);
  }, [profile?.company_id, featureName]);

  useEffect(() => {
    let isMounted = true;

    // Failsafe timeout: Default to TRUE for core features on PIN sessions if edge function times out
    timeoutRef.current = setTimeout(() => {
      if (isMounted && loadingRef.current) {
        const coreFeatures = ['tasks', 'communications', 'maintenance', 'daily_checklists', 'checklists', 'gamification', 'kiosk', 'training', 'temperature_logs'];
        const isCore = coreFeatures.includes(featureName);
        const isPinMode = isPinSession();

        // For PIN sessions, assume core features are available if we can't verify
        const defaultAccess = isPinMode && isCore;

        console.warn(`[FEATURE_ACCESS_PIN] ⚠️ Timeout for ${featureName} - defaulting to ${defaultAccess} (PIN: ${isPinMode}, Core: ${isCore})`);
        setLoading(false);
        setHasAccess(defaultAccess);
      }
    }, 5000);

    async function checkAccess() {
      const isPin = isPinSession();
      let companyId: string | null = null;
      let companyCode: string | null = null;

      console.log(`[FEATURE_ACCESS_PIN] Starting checkAccess for ${featureName}:`, { isPin });

      // The "universal optimistic grant" block that used to live here is
      // gone — it unconditionally returned true for tasks / communications /
      // maintenance / training / daily_checklists / analytics /
      // temperature_logs on any PIN session, which meant per-company
      // company_feature_overrides did not apply to kiosk users. The stale-
      // cache lockout it was patching around is now handled by:
      //   1. Per-company localStorage cache keys (new in this file) so
      //      switching kiosks between companies can't carry over data.
      //   2. A background revalidation on login, on reload when the saved
      //      answer is older than a minute, on focus, and every few minutes
      //      while the kiosk stays open. Mounted gates pick up the result
      //      via CACHE_UPDATED_EVENT. Other companies' entries are dropped
      //      on a company change.
      //   3. The 5-second timeout failsafe below that defaults core features
      //      to true if the edge function never responds.
      // Together these cover the lockout cases without sacrificing override
      // accuracy.

      // OPTIMISTIC: Immediately grant access to extra Fresh-Fill-tier features
      // (gamification, kiosk_login, mobile_app_access) that aren't part of the
      // universal PIN set above. Kept narrow on purpose — non-Fresh-Fill PIN
      // users still go through the regular plan check for these.
      if (isPin) {
        const pinData = getPinSessionData();
        companyCode = pinData?.company_code || null;

        const freshFillCodes = ['FRESHFILL2026', 'FFLOSBANOS', 'FFMADERA'];
        const freshFillExtras = ['gamification', 'kiosk', 'mobile'];

        if (companyCode && freshFillCodes.includes(companyCode.toUpperCase()) && freshFillExtras.includes(featureName)) {
          console.log(`[FEATURE_ACCESS_PIN] ⚡ Optimistic access granted for ${featureName} (Fresh Fill)`);
          if (isMounted) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setHasAccess(true);
            setLoading(false);
          }
          return;
        }
      }

      // Resolve companyId first — we need it to read the per-company cache.
      ({ companyId, companyCode } = resolveCompany(profile?.company_id));

      // FIRST: Check localStorage cache (scoped by company_id)
      const cachedAccess = readCachedAccess(companyId, featureName);
      if (cachedAccess !== undefined) {
        console.log(`[FEATURE_ACCESS_PIN] ⚡ Using localStorage cache for ${featureName}:`, cachedAccess);
        if (isMounted) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setHasAccess(cachedAccess);
          setLoading(false);
        }
        // Paint the saved answer, then re-check if it is more than a minute
        // old. A legacy entry has no confirmation clock and is always stale,
        // which is what a reload used to miss for up to six hours. Six gates
        // call this together; the throttle and the single-flight make it one
        // request. A failure leaves the answer just painted.
        schedulePinRevalidate(companyId, companyCode, FEATURE_FOCUS_STALE_MS);
        return;
      }

      // There used to be a SECOND tier here that answered from
      // `pinData.subscription.features` — the subscription blob pin-login
      // returns. It is gone, and must not come back: that blob is
      // `subscription_plans.features` copied verbatim (pin-login/index.ts),
      // with NO company_feature_overrides applied. Because it returned before
      // limited-feature-access was ever called, arming it would have quietly
      // demoted every kiosk gate to plan-only — e.g. JUICEITUPEAG, whose plan
      // says daily_checklists:false / schedules:false and relies entirely on
      // override rows, would have lost checklists and schedules at the kiosk
      // with no error. It never fired in practice only because no writer
      // persists `subscription` into the stored session, so it was one
      // well-meaning `...data` spread away from breaking. Overrides are
      // resolved server-side by limited-feature-access (which calls
      // has_feature_access per feature), and that is now the only path.
      if (isPin) {
        console.log(`[FEATURE_ACCESS_PIN] PIN session data for ${featureName}:`, { companyId, companyCode });
      }

      if (!companyId && !companyCode) {
        console.warn(`[FEATURE_ACCESS_PIN] No company ID or code found for ${featureName}, setting loading=false, hasAccess=false`);
        if (isMounted) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setLoading(false);
          setHasAccess(false);
        }
        return;
      }

      const cacheKey = `${companyId || companyCode}-${featureName}`;

      if (featureAccessCache.has(cacheKey)) {
        const cachedValue = featureAccessCache.get(cacheKey)!;
        console.log(`[FEATURE_ACCESS_PIN] Cache hit for ${featureName}:`, cachedValue);
        if (isMounted) {
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setHasAccess(cachedValue);
          setLoading(false);
        }
        schedulePinRevalidate(companyId, companyCode, FEATURE_FOCUS_STALE_MS);
        return;
      }

      console.log(`[FEATURE_ACCESS_PIN] Cache miss for ${featureName}, fetching...`);

      // Genuinely cold for THIS company: fail closed. On first mount this is
      // already the state; it matters when the session switches company under
      // a mounted gate, which must not keep showing the previous company's
      // answer while this one is in flight.
      if (isMounted && !loadingRef.current) {
        setHasAccess(false);
        setLoading(true);
      }

      try {
        if (isPin) {
          console.log(`[FEATURE_ACCESS_PIN] Fetching ALL features for company to populate cache:`, { companyId, companyCode });

          // One request per company, however many gates are mounted -- and
          // it is the SAME request the login-time background revalidation
          // started, when there is one. fetchPlanFeatures writes both caches.
          const planFeatures = await fetchPlanFeatures(companyId, companyCode);

          const mappedFeature = FEATURE_MAPPING[featureName] || featureName;
          if (!(mappedFeature in planFeatures)) {
            // The gate WE were asked about is the one that didn't resolve.
            // Fall into the catch below so it retries on the existing backoff,
            // rather than reporting — and caching — a denial we never computed.
            throw new Error(`Feature access unresolved for ${featureName}`);
          }

          if (isMounted) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            const access = planFeatures[mappedFeature] === true;
            featureAccessCache.set(cacheKey, access);
            setHasAccess(access);
            setLoading(false);
            retryCount.current = 0;
            console.log(`[FEATURE_ACCESS_PIN] ✅ ${featureName} access:`, access, '(cached all features)');
          }
        } else {
          const { data, error } = await supabase.rpc('has_feature_access', {
            _company_id: companyId,
            _feature_name: featureName
          });

          if (isMounted) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            if (!error && data !== null) {
              featureAccessCache.set(cacheKey, data);
              setHasAccess(data);
              retryCount.current = 0;
            } else {
              console.warn(`[FEATURE_ACCESS] Check failed for ${featureName}:`, error?.message);
              setHasAccess(false);
            }
            setLoading(false);
          }
        }
      } catch (error) {
        console.error(`[FEATURE_ACCESS_PIN] ❌ Error checking ${featureName}:`, error);
        if (isMounted) {
          retryCount.current++;
          if (retryCount.current < maxRetries) {
            console.log(`[FEATURE_ACCESS_PIN] 🔄 Retrying ${featureName} (${retryCount.current}/${maxRetries})`);
            setTimeout(() => checkAccess(), 500 * retryCount.current);
            return;
          }
          console.error(`[FEATURE_ACCESS_PIN] ❌ Max retries reached for ${featureName}, setting loading=false, hasAccess=false`);
          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          setHasAccess(false);
          setLoading(false);
        }
      }
    }

    checkAccess();

    return () => {
      isMounted = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [profile?.company_id, featureName, pinSessionCompanyId]);

  return { hasAccess, loading };
}
